/**
 * Drive management — sync + tracked-content surface.
 *
 * Mirrors the V1 ``app/components/host.ts`` ``driveManagement`` block.
 * Drive's UX is the most complex of the three sync families: the user
 * picks WHICH folders + files to track BEFORE triggering sync. The
 * ``tracked-content`` family of endpoints owns that selection state
 * server-side so it survives device hops + browser refresh.
 *
 * Provider field is shared across Google Drive / OneDrive / SharePoint /
 * Notion — each one carries the same ``connection_key`` shape
 * (account/email/workspace-bot-id) the unified surface uses for routing.
 */

import { apiClient } from "../ApiClient.js";

export type DriveProvider = "google_drive" | "onedrive" | "sharepoint" | "notion";

const DRIVE_PROVIDERS: ReadonlySet<string> = new Set<DriveProvider>([
  "google_drive",
  "onedrive",
  "sharepoint",
  "notion",
]);

/**
 * Narrow an arbitrary connection ``provider``/``vendor`` string to a valid
 * ``DriveProvider``, or ``null`` when it isn't one. Single source of truth so
 * the picker, sync sheets, and folder browser never route on an unchecked
 * cast (which surfaced as "Provider non supportato: undefined").
 */
export function asDriveProvider(value: string | null | undefined): DriveProvider | null {
  return value && DRIVE_PROVIDERS.has(value) ? (value as DriveProvider) : null;
}

// ──────────────────────────────────────────────────────────────────────
// Tracked content (folders + standalone files the user has opted into)
// ──────────────────────────────────────────────────────────────────────

export interface TrackedFolder {
  readonly id: string;
  readonly remote_id: string;
  readonly name: string;
  readonly path?: string | null;
  /** ``new`` + ``modified`` + ``synced`` reported by ``folder-contents``. */
  readonly file_counts?: {
    readonly new_count: number;
    readonly modified_count: number;
    readonly synced_count: number;
  } | null;
  readonly last_sync_at?: string | null;
  readonly added_at?: string | null;
}

export interface TrackedFile {
  readonly id: string;
  readonly remote_id: string;
  readonly name: string;
  readonly mime_type?: string | null;
  readonly parent_folder_id?: string | null;
  readonly size_bytes?: number | null;
  readonly status?: string;
  readonly last_sync_at?: string | null;
}

export interface TrackedContentResponse {
  readonly folders: readonly TrackedFolder[];
  readonly files: readonly TrackedFile[];
  /** Aggregate bytes across all tracked content (UI ``StorageImpactBar``). */
  readonly total_size_bytes?: number;
}

/**
 * GET ``/drive-management/tracked-content?user_id=&provider=&connection_key=``.
 *
 * Returns every folder + standalone file the user has registered for
 * this drive. Drives a "manage what we sync" UI that closely mirrors
 * V1's ``DocSyncManager`` dialog.
 *
 * Wire-shape note (May 25, 2026 BE audit): the BE actually returns a
 * single ``items[]`` list with each row carrying a ``type: "folder" |
 * "file"`` discriminator + a flat ``total_folders`` / ``total_files``
 * counter pair (V1's contract). The V2 adapter splits the list into
 * the typed buckets the FE expects — keeping the BE contract stable
 * while giving consumers the same ``{folders, files}`` shape every
 * other endpoint here uses.
 */
interface RawTrackedItemFolder {
  readonly type: "folder";
  readonly id: string;
  readonly remote_id?: string;
  readonly name: string;
  readonly path?: string | null;
  /**
   * V1 BE flat shape (drive_management.py:1814) — three independent
   * counters at top level. Older mock contracts used the nested
   * ``file_counts`` form; the adapter accepts both.
   */
  readonly file_count?: number;
  readonly synced_count?: number;
  readonly pending_count?: number;
  readonly file_counts?: TrackedFolder["file_counts"];
  readonly ingestion_status?: string | null;
  readonly last_ingested_at?: string | null;
  readonly last_sync_at?: string | null;
  readonly added_at?: string | null;
  readonly total_size_bytes?: number;
}
interface RawTrackedItemFile {
  readonly type: "file";
  readonly id: string;
  readonly remote_id?: string;
  readonly name: string;
  readonly mime_type?: string | null;
  readonly parent_folder_id?: string | null;
  readonly size_bytes?: number | null;
  readonly file_size?: number | null;
  readonly ingestion_status?: string | null;
  readonly status?: string | null;
  readonly last_sync_at?: string | null;
  readonly last_ingested_at?: string | null;
  readonly parent_folder_name?: string | null;
}
type RawTrackedItem = RawTrackedItemFolder | RawTrackedItemFile;
interface RawTrackedContentResponse {
  readonly items: readonly RawTrackedItem[];
  readonly total_folders?: number;
  readonly total_files?: number;
  readonly total_size_bytes?: number;
}

export async function getTrackedContent(
  userId: string,
  provider: DriveProvider,
  connectionKey?: string,
): Promise<TrackedContentResponse> {
  const params = new URLSearchParams({ user_id: userId, provider });
  if (connectionKey) params.set("connection_key", connectionKey);
  const raw = await apiClient.get<RawTrackedContentResponse | TrackedContentResponse>(
    `/drive-management/tracked-content?${params.toString()}`,
  );

  // BE V1 shape — flat ``items[]``. Split into typed buckets here so
  // every FE caller can keep the ergonomic ``{folders, files}`` form.
  if ("items" in raw && Array.isArray(raw.items)) {
    const folders: TrackedFolder[] = [];
    const files: TrackedFile[] = [];
    for (const it of raw.items) {
      if (it.type === "folder") {
        // Re-shape the flat counters BE returns into the nested
        // ``file_counts`` form the FE renders against. ``new_count`` is
        // a derived bucket — BE doesn't track "new vs modified"
        // separately at tracked-content level (that lives on the
        // per-folder ``/folder-contents`` probe), so we attribute the
        // ``pending`` total to "new" by default. The DocSyncManager
        // upgrades each folder's split when the user expands it and
        // ``useFolderContents`` returns the per-status breakdown.
        const counts =
          it.file_counts ??
          (it.file_count !== undefined
            ? {
                new_count: it.pending_count ?? 0,
                modified_count: 0,
                synced_count: it.synced_count ?? 0,
              }
            : null);
        folders.push({
          id: it.id,
          remote_id: it.remote_id ?? it.id,
          name: it.name,
          path: it.path ?? null,
          file_counts: counts,
          last_sync_at: it.last_sync_at ?? it.last_ingested_at ?? null,
          added_at: it.added_at ?? null,
        });
      } else {
        // BE uses ``file_size`` (drive_management.py:1872) on
        // tracked-content; V2 client model normalises to ``size_bytes``.
        // ``status`` falls back to ``ingestion_status`` which the BE
        // populates with ``completed`` / ``pending`` / ``synced``.
        files.push({
          id: it.id,
          remote_id: it.remote_id ?? it.id,
          name: it.name,
          mime_type: it.mime_type ?? null,
          parent_folder_id: it.parent_folder_id ?? null,
          size_bytes: it.file_size ?? it.size_bytes ?? null,
          status: it.status ?? it.ingestion_status ?? undefined,
          last_sync_at: it.last_sync_at ?? it.last_ingested_at ?? null,
        });
      }
    }
    return {
      folders,
      files,
      total_size_bytes: raw.total_size_bytes,
    };
  }

  // Fallback: the BE may already speak the V2 ``{folders, files}``
  // contract (future refactor / mocked tests) — pass it through.
  return raw as TrackedContentResponse;
}

export interface AddTrackedFolderBody {
  readonly user_id: string;
  readonly provider: DriveProvider;
  readonly remote_id: string;
  readonly name: string;
  readonly connection_key?: string;
}

/** POST ``/drive-management/tracked-folders``. */
export async function addTrackedFolder(body: AddTrackedFolderBody): Promise<TrackedFolder> {
  return apiClient.post<TrackedFolder>("/drive-management/tracked-folders", body);
}

/**
 * Common shape for remove-tracked endpoints. BE Pydantic signatures
 * (drive_management.py:1412 folder, :2031 file) condividono il
 * contratto query: ``user_id`` + ``provider`` REQUIRED, optional
 * ``delete_documents`` (cascade delete from Weaviate/Neo4j/MinIO) e
 * ``connection_key`` (multi-account scoping).
 *
 * Pre-fix (giugno 11, 2026 prima edit): FE chiamava entrambi gli
 * endpoint bare → BE 422 ValidationError "missing user_id / provider"
 * + toast "errore eliminazione". DRY refactor (audit giugno 11):
 * helper unico per costruire la query string così non si replica la
 * logica in entrambi gli endpoint, e quando il BE estende lo schema
 * (es. ``hard_delete`` flag in arrivo) basta una sola edit.
 *
 * I default ``delete_documents`` differiscono fra folder (false:
 * untrack soft, preserva i dati) e file (true: drop full). Il caller
 * passa ``deleteDocuments`` solo quando vuole OVERRIDARE il default
 * BE — l'helper sa, via ``defaultDeleteDocuments``, quando spedire
 * il flag e quando ometterlo (BE applica il default in assenza).
 */
interface RemoveTrackedCommonParams {
  readonly userId: string;
  readonly provider: string;
  readonly deleteDocuments?: boolean;
  readonly connectionKey?: string;
}

function buildRemoveQuery(
  params: RemoveTrackedCommonParams,
  defaultDeleteDocuments: boolean,
): string {
  const qs = new URLSearchParams({
    user_id: params.userId,
    provider: params.provider,
  });
  // Send the flag ONLY quando l'utente vuole override del default BE.
  // Se ``deleteDocuments`` è undefined, lasciamo che il BE applichi
  // il suo default (false su folder, true su file).
  if (
    params.deleteDocuments !== undefined &&
    params.deleteDocuments !== defaultDeleteDocuments
  ) {
    qs.set("delete_documents", params.deleteDocuments ? "true" : "false");
  }
  if (params.connectionKey) qs.set("connection_key", params.connectionKey);
  return qs.toString();
}

/**
 * DELETE ``/drive-management/tracked-folders/{folderId}``.
 *
 * Jun 29, 2026 — default FLIPPATO da ``false`` a ``true`` per mirrorare
 * il nuovo default BE. UX naturale: untracking una folder cancella i
 * file dal sistema. Passa ``deleteDocuments: false`` esplicitamente
 * per il vecchio comportamento "soft untrack" (raro: usato solo se
 * vuoi conservare l'ingestion in caso di re-tracking imminente).
 */
export interface RemoveTrackedFolderParams extends RemoveTrackedCommonParams {
  readonly folderId: string;
}

export async function removeTrackedFolder(
  params: RemoveTrackedFolderParams,
): Promise<void> {
  const qs = buildRemoveQuery(params, /* defaultDeleteDocuments */ true);
  await apiClient.delete<void>(
    `/drive-management/tracked-folders/${encodeURIComponent(params.folderId)}?${qs}`,
  );
}

/**
 * BE Pydantic ``FileSelectionRequest`` (drive_management.py:878).
 *
 * Wire contract MUST match BE exactly: ``selections`` (not ``files``)
 * on the envelope, ``file_size`` (not ``size_bytes``) on each item.
 * Mismatch caused the post-picker persistence to fail silently — files
 * shown in the picker but not in the tracked content on refresh.
 */
export interface SelectFilesBody {
  readonly user_id: string;
  readonly provider: DriveProvider;
  readonly connection_key?: string;
  readonly selections: readonly {
    readonly file_id: string;
    readonly file_name: string;
    readonly file_path?: string;
    readonly parent_folder_id?: string | null;
    readonly mime_type?: string;
    readonly file_size?: number;
    readonly web_view_link?: string;
    readonly owner?: string;
    readonly modified_time?: string;
    /** SharePoint-only — site + drive scoping. */
    readonly site_id?: string;
    readonly drive_id?: string;
  }[];
}

interface SaveSelectionsAck {
  readonly status?: string;
  readonly count?: number;
  readonly user_id?: string;
  readonly provider?: string;
  readonly selections_count?: number;
}

/** POST ``/drive-management/select-files`` — bulk add by remote id. */
export async function selectTrackedFiles(body: SelectFilesBody): Promise<SaveSelectionsAck> {
  return apiClient.post<SaveSelectionsAck>("/drive-management/select-files", body);
}

/**
 * BE Pydantic ``FolderSelectionRequest`` (drive_management.py:859).
 *
 * ``selections`` (not ``folders``) on the envelope, ``folder_path`` is
 * REQUIRED on each item (BE Pydantic field). When unknown, callers
 * should fall back to the folder name or id as the path.
 */
export interface SelectFoldersBody {
  readonly user_id: string;
  readonly provider: DriveProvider;
  readonly connection_key?: string;
  readonly selections: readonly {
    readonly folder_id: string;
    readonly folder_path: string;
    readonly folder_name?: string;
    readonly pre_embed?: boolean;
    readonly site_id?: string;
    readonly drive_id?: string;
  }[];
}

/**
 * POST ``/drive-management/select-folders`` — bulk add whole-folder
 * trackings. Companion to ``selectTrackedFiles`` for the post-picker
 * persistence step.
 */
export async function selectTrackedFolders(body: SelectFoldersBody): Promise<SaveSelectionsAck> {
  return apiClient.post<SaveSelectionsAck>("/drive-management/select-folders", body);
}

/**
 * DELETE ``/drive-management/tracked-files/{fileId}``.
 *
 * BE default ``delete_documents=true`` (cascade — un file tracked
 * senza i suoi dati ingeriti non ha senso). Passa
 * ``deleteDocuments: false`` per opt-out (untrack soft).
 */
export interface RemoveTrackedFileParams extends RemoveTrackedCommonParams {
  readonly fileId: string;
}

export async function removeTrackedFile(
  params: RemoveTrackedFileParams,
): Promise<void> {
  const qs = buildRemoveQuery(params, /* defaultDeleteDocuments */ true);
  await apiClient.delete<void>(
    `/drive-management/tracked-files/${encodeURIComponent(params.fileId)}?${qs}`,
  );
}

/**
 * BE response shape for ``GET /drive-management/folder-contents``
 * (matches ``_build_folder_contents_response`` in
 * ``drive_management.py:578``). The BE emits a single ``files`` array
 * where each entry carries a ``sync_status`` discriminator; pre-fix V2
 * expected three separate buckets (``new_files`` / ``modified_files`` /
 * ``synced_files``) that the BE never returned — every expand threw
 * ``contents.data.new_files is not iterable`` (June 5 2026). The
 * UI-side bucketing (if needed) is the consumer's responsibility.
 *
 * Field shape per file matches V1 ``FolderChild`` (see
 * ``ando-frontend/app/components/sync/DocSyncManager.tsx:223``).
 */
export interface FolderContentFile {
  readonly id: string;
  readonly name: string;
  readonly size_bytes: number;
  readonly mime_type: string | null;
  readonly is_folder: boolean;
  readonly web_view_link?: string | null;
  readonly sync_status: "new" | "modified" | "synced" | null;
  readonly folder_path?: string | null;
}

export interface FolderContents {
  readonly folder_id: string;
  readonly files: readonly FolderContentFile[];
  readonly total_size_bytes: number;
  readonly new_size_bytes: number;
}

/**
 * GET ``/drive-management/folder-contents?user_id=&provider=&folder_id=[&connection_key=&recursive=]``.
 *
 * The BE requires ``user_id`` + ``provider`` + ``folder_id`` as required
 * query params and returns 422 ``Unprocessable Entity`` when any is
 * missing. V1 ``host.ts:folderContents`` passes all five; V2 pre-fix
 * only passed ``folder_id`` — the picker's "Carico contenuti…" message
 * stayed forever because every call 422'd silently (June 5 2026 fix).
 */
export interface GetFolderContentsParams {
  readonly userId: string;
  readonly provider: DriveProvider;
  readonly folderId: string;
  readonly connectionKey?: string;
  readonly recursive?: boolean;
}

export async function getFolderContents(
  params: GetFolderContentsParams,
): Promise<FolderContents> {
  const qs = new URLSearchParams({
    user_id: params.userId,
    provider: params.provider,
    folder_id: params.folderId,
  });
  if (params.connectionKey) qs.set("connection_key", params.connectionKey);
  if (params.recursive) qs.set("recursive", "true");
  return apiClient.get<FolderContents>(
    `/drive-management/folder-contents?${qs.toString()}`,
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sync triggers + status
// ──────────────────────────────────────────────────────────────────────

export interface DriveStartIngestionBody {
  readonly user_id: string;
  readonly provider: DriveProvider;
  readonly folder_ids?: readonly string[];
  readonly file_ids?: readonly string[];
  readonly connection_key?: string;
}

export interface DriveSyncJobAck {
  readonly job_id?: string | null;
  readonly status?: string;
  readonly detail?: string;
}

/**
 * POST ``/drive-management/start-ingestion`` — kick off the worker.
 *
 * When ``folder_ids`` is empty + ``file_ids`` is empty, the BE syncs the
 * whole tracked surface (legacy behaviour). Otherwise it scopes the run
 * to the explicit ids.
 */
export async function startDriveIngestion(body: DriveStartIngestionBody): Promise<DriveSyncJobAck> {
  return apiClient.post<DriveSyncJobAck>("/drive-management/start-ingestion", body);
}

/** POST ``/drive-management/force-sync`` — full re-sync of the tracked surface. */
export async function forceDriveSync(body: DriveStartIngestionBody): Promise<DriveSyncJobAck> {
  return apiClient.post<DriveSyncJobAck>("/drive-management/force-sync", body);
}

/** POST ``/drive-management/sync-file`` — single-file re-sync. */
export async function syncDriveFile(body: {
  readonly user_id: string;
  readonly provider: DriveProvider;
  readonly file_id: string;
  readonly connection_key?: string;
}): Promise<DriveSyncJobAck> {
  return apiClient.post<DriveSyncJobAck>("/drive-management/sync-file", body);
}

export interface DriveSyncStatus {
  readonly status?: string;
  readonly job_id?: string | null;
  readonly job_status?: string | null;
  readonly files_synced?: number;
  readonly last_sync_at?: string | null;
  /** Per-folder delta — mirrors ``TrackedFolder.file_counts``. */
  readonly folder_deltas?: Readonly<
    Record<string, { new_count: number; modified_count: number; synced_count: number }>
  >;
  // ── Auto-sync (V1 audit G1, BE /drive-management/sync-status:3185-3188) ──
  // ``auto_sync_enabled`` → nightly sync is active for this account.
  // ``auto_sync_eligible`` → start_page_token exists (i.e. at least one
  //                          full sync ran; incremental can run from now on).
  // ``auto_sync_interval_minutes`` → cadence (1440 = nightly).
  // ``next_auto_sync_at`` → next ISO timestamp the scheduler will fire.
  readonly auto_sync_enabled?: boolean;
  readonly auto_sync_eligible?: boolean;
  readonly auto_sync_interval_minutes?: number;
  readonly next_auto_sync_at?: string | null;
  /** Real indexed count from ``content_dedup_index`` (BE single source of truth). */
  readonly files_indexed?: number;
  // ── Resume vs Restart (V1 audit G4, BE :3192-3196) ──
  readonly has_resumable_job?: boolean;
  readonly resumable_job_id?: string | null;
  readonly resumable_job_progress?: number | null;
  readonly resumable_job_total?: number | null;
  readonly resumable_job_processed?: number | null;
}

/** GET ``/drive-management/sync-status?user_id=&provider=&connection_key=``. */
export async function getDriveSyncStatus(
  userId: string,
  provider: DriveProvider,
  connectionKey?: string,
): Promise<DriveSyncStatus> {
  const params = new URLSearchParams({ user_id: userId, provider });
  if (connectionKey) params.set("connection_key", connectionKey);
  return apiClient.get<DriveSyncStatus>(`/drive-management/sync-status?${params.toString()}`);
}

/** POST ``/drive-management/cancel-sync?job_id=&user_id=``. */
export async function cancelDriveSync(jobId: string, userId: string): Promise<DriveSyncJobAck> {
  const params = new URLSearchParams({ job_id: jobId, user_id: userId });
  return apiClient.post<DriveSyncJobAck>(`/drive-management/cancel-sync?${params.toString()}`, {});
}

/**
 * Wire shape for ``GET /drive-management/ingestion-status``. Mirrors the
 * BE ``build_ingestion_status_response`` (see
 * ``ando-agent/src/api/v1/endpoints/sync_helpers.py``) — the canonical
 * envelope every Drive job streams its progress through.
 *
 * The ``status`` field carries the same union the FE ``syncStore``
 * tracks (``pending`` | ``processing`` | ``running`` | ``completed`` |
 * ``failed`` | ``cancelled`` | ``cancelling``). ``"not_found"`` is the
 * only out-of-band value (returned when no job exists for the lookup).
 *
 * Pre-fix (June 10, 2026 audit ciclo 3): the cancel-poll loop in
 * ``DocSyncManager`` was hitting ``/drive-management/sync-status``
 * which exposes ``last_sync_status`` (success/failed/error), NOT the
 * live ``job_status``. The poll always exited on iteration 1 → false
 * "Sincronizzazione annullata" toast 1.5 s after click while Temporal
 * was still cancelling. ``/ingestion-status`` is the right path —
 * carries the live ``status`` queried from the Temporal workflow.
 */
export interface DriveIngestionStatus {
  readonly job_id: string;
  readonly status: string;
  readonly progress_percentage?: number;
  readonly total_files?: number;
  readonly processed_files?: number;
  readonly successful_files?: number;
  readonly failed_files?: number;
  readonly skipped_files?: number;
  readonly created_at?: string | null;
  readonly updated_at?: string | null;
  readonly completed_at?: string | null;
  readonly error_message?: string | null;
}

/**
 * GET ``/drive-management/ingestion-status?user_id=&provider=&job_id=``.
 *
 * Authoritative source for the live ``status`` of a Drive ingestion
 * job. ``job_id`` is REQUIRED here (the no-id BE branch returns the
 * "latest job for user" which is a different semantic and the cancel
 * poll wants a SPECIFIC job).
 */
export async function getDriveIngestionStatus(
  jobId: string,
  userId: string,
  provider: DriveProvider,
  options: { readonly signal?: AbortSignal } = {},
): Promise<DriveIngestionStatus> {
  const params = new URLSearchParams({
    user_id: userId,
    provider,
    job_id: jobId,
  });
  return apiClient.get<DriveIngestionStatus>(
    `/drive-management/ingestion-status?${params.toString()}`,
    { signal: options.signal },
  );
}

// ──────────────────────────────────────────────────────────────────────
// Folder tree (Google Picker fallback / OneDrive Graph walk)
// ──────────────────────────────────────────────────────────────────────

export interface DriveFolderNode {
  readonly id: string;
  readonly name: string;
  readonly parent_id?: string | null;
  readonly children_count?: number;
  readonly has_children?: boolean;
  readonly already_tracked?: boolean;
  // Total files in this folder + all descendants. Surfaced so the
  // picker can show the recursive sync scope BEFORE the user confirms
  // (otherwise checking a root folder triggers a recursive sync of
  // hundreds of files with no warning — Jun 16, 2026 UX gotcha).
  readonly descendant_file_count?: number;
}

export interface FolderTreeResponse {
  readonly nodes: readonly DriveFolderNode[];
  /** Roots for the BE-rendered tree (e.g. "My Drive", "Shared with me"). */
  readonly roots: readonly string[];
  // Raw BE-side metadata, surfaced so the picker can detect
  // stale-empty cache (force_refresh trigger — see GraphTreePickerModal).
  readonly cached?: boolean;
  readonly stale?: boolean;
  readonly cache_age_hours?: number;
}

// BE-side raw shape (matches Python ``FolderTree`` dataclass in
// ``src/services/drive_connectors/base.py``). The picker consumed shape
// ``{nodes[], roots[]}`` is computed FE-side from this nested tree —
// V1 read this shape verbatim (``CachedFolderTreeResponse.tree`` typed
// as ``FolderTreeNode``); V2 picked a flat shape that the BE never
// returned, so every drive picker rendered "Nessuna cartella" even when
// the BE had successfully fetched 68 items from Graph (June 5 2026
// incident). Now we accept the BE shape verbatim and flatten in this
// module — no BE change required.
interface RawBackendFolderNode {
  readonly folder_id: string;
  readonly name: string;
  readonly parent_id?: string | null;
  readonly children?: readonly RawBackendFolderNode[];
  readonly file_count?: number;
  readonly has_files?: boolean;
}

interface RawBackendFolderTreeResponse {
  readonly tree: RawBackendFolderNode | null;
  readonly cache_age_hours?: number;
  readonly cached?: boolean;
  readonly stale?: boolean;
  readonly sync_type?: string;
  readonly last_synced?: string | null;
}

/**
 * Walk the BE's nested ``FolderTree`` and produce the flat
 * ``{nodes[], roots[]}`` shape the picker UI is built against. The BE's
 * synthetic top-level node (``folder_id === "root"``) is NOT emitted —
 * its children become the picker's roots so the user sees real folders
 * at the top, not a redundant "Root" wrapper.
 */
function flattenBackendTree(
  raw: RawBackendFolderTreeResponse,
): FolderTreeResponse {
  const nodes: DriveFolderNode[] = [];
  const roots: string[] = [];

  // Post-order tally of descendant files so the picker can show the
  // recursive sync scope on each node (Jun 16, 2026). Computed once on
  // the same tree pass; we walk children first and return the subtree
  // total to the parent.
  const tallyDescendantFiles = (node: RawBackendFolderNode): number => {
    let total = node.file_count ?? 0;
    for (const child of node.children ?? []) {
      total += tallyDescendantFiles(child);
    }
    return total;
  };
  const descendantFileCount = new Map<string, number>();
  const seedCounts = (node: RawBackendFolderNode): void => {
    descendantFileCount.set(node.folder_id, tallyDescendantFiles(node));
  };
  if (raw.tree) {
    const walk = (n: RawBackendFolderNode): void => {
      seedCounts(n);
      for (const c of n.children ?? []) walk(c);
    };
    walk(raw.tree);
  }

  const visit = (
    node: RawBackendFolderNode,
    parentId: string | null,
    isUnderSyntheticRoot: boolean,
  ): void => {
    const children = node.children ?? [];
    // Skip the synthetic top-level "root" node — its children become
    // the visible roots of the picker. The BE always wraps the tree in
    // a synthetic ``folder_id === "root"`` per the
    // ``_build_tree_from_flat_data`` contract; emitting it would force
    // the user to click through a redundant level.
    if (node.folder_id !== "root") {
      nodes.push({
        id: node.folder_id,
        name: node.name,
        parent_id: isUnderSyntheticRoot ? null : (parentId ?? node.parent_id ?? null),
        children_count: children.length,
        has_children: children.length > 0,
        descendant_file_count: descendantFileCount.get(node.folder_id) ?? 0,
      });
      if (isUnderSyntheticRoot) roots.push(node.folder_id);
    }
    for (const child of children) {
      visit(child, node.folder_id, node.folder_id === "root");
    }
  };

  if (raw.tree) visit(raw.tree, null, false);

  return {
    nodes,
    roots,
    cached: raw.cached,
    stale: raw.stale,
    cache_age_hours: raw.cache_age_hours,
  };
}

/**
 * GET ``/drive-management/folder-tree-cached?user_id=&provider=[&connection_key=&force_refresh=]``.
 *
 * BE endpoint (drive_management.py:960) — returns the cached folder
 * tree with cache-age metadata; pass ``force_refresh=true`` to bypass
 * the 24h cache and re-fetch from the IdP.
 *
 * SharePoint is rejected by the BE (raises ValidationError) — the FE
 * must drive the picker through the per-site/per-drive endpoints
 * (``listSharePointSites`` / ``listSharePointDrives``) before this
 * tree call can fire. The ``root`` argument is accepted for forward
 * compat but ignored by the BE today (the tree is rooted at the
 * provider's natural root).
 */
export async function getDriveFolderTree(
  userId: string,
  provider: DriveProvider,
  connectionKey?: string,
  _root?: string,
  forceRefresh = false,
): Promise<FolderTreeResponse> {
  const params = new URLSearchParams({ user_id: userId, provider });
  if (connectionKey) params.set("connection_key", connectionKey);
  if (forceRefresh) params.set("force_refresh", "true");
  const raw = await apiClient.get<RawBackendFolderTreeResponse>(
    `/drive-management/folder-tree-cached?${params.toString()}`,
  );
  return flattenBackendTree(raw);
}

// ──────────────────────────────────────────────────────────────────────
// Native-picker helpers
// ──────────────────────────────────────────────────────────────────────

export interface GooglePickerToken {
  /** Ephemeral OAuth access token suitable for ``gapi.client.setToken``. */
  readonly access_token: string;
  /** API key required by the Picker JS SDK. */
  readonly developer_key?: string;
  /** Google project number (Picker SDK ``appId`` field). */
  readonly app_id?: string;
  /** ISO timestamp; FE should refresh ~30s before. */
  readonly expires_at?: string;
}

/**
 * GET ``/drive-management/google-picker-token?user_id=&connection_key=``.
 *
 * The Google Picker JS SDK needs a short-lived access token + the
 * developer key — the BE mints them server-side so the secret never
 * ships to the FE bundle. Token TTL ~1h; FE refetches on every picker
 * open to keep the boundary clean.
 */
export async function getGooglePickerToken(
  userId: string,
  connectionKey?: string,
): Promise<GooglePickerToken> {
  const params = new URLSearchParams({ user_id: userId });
  if (connectionKey) params.set("connection_key", connectionKey);
  return apiClient.get<GooglePickerToken>(
    `/drive-management/google-picker-token?${params.toString()}`,
  );
}

// SharePoint multi-step picker — sites → drives → folders. Each step is
// a Graph relay so the SPA never holds a Graph token directly.

/** Canonical V2 ``SharePointSite`` — what the FE consumes. */
export interface SharePointSite {
  readonly id: string;
  readonly name: string;
  readonly web_url?: string | null;
  readonly display_name?: string | null;
  readonly description?: string | null;
}

/** Raw shape returned by BE ``GET /drive/sharepoint/sites`` (drive.py:823). */
interface RawSharePointSite {
  readonly site_id: string;
  readonly name: string;
  readonly display_name?: string;
  readonly web_url?: string;
  readonly description?: string | null;
}

/**
 * GET ``/drive/sharepoint/sites?user_id=&connection_key=``.
 *
 * BE response (drive.py:860): ``{sites: [{site_id, name, display_name,
 * web_url, description}], total_count}``. V2 adapter remaps
 * ``site_id`` → ``id`` so the picker UI stays provider-agnostic.
 */
// ──────────────────────────────────────────────────────────────────────
// SharePoint files endpoint (June 5 2026)
//
// SharePoint cannot reuse ``/drive-management/folder-tree-cached``: the
// BE rejects ``provider=sharepoint`` on that route with a ValidationError
// ("SharePoint folder tree requires selecting a site first"). V1 had a
// dedicated ``listSharePointFiles`` call ([ando-frontend/app/api/drive/
// sharepoint.ts:100]) against ``/drive/files`` that returns a flat list
// of files+folders for the chosen site+drive — the picker then filters
// folders client-side and builds the tree there. V2 was wired to the
// rejected route, so the SharePoint picker never rendered a folder.
//
// Endpoint + shape mirror V1 verbatim; the adapter
// ``sharePointFilesToFolderTree`` below lifts the response into the
// shared ``FolderTreeResponse`` shape so ``<FolderTreeList>`` consumes it
// unchanged.
// ──────────────────────────────────────────────────────────────────────

export interface SharePointFile {
  readonly file_id: string;
  readonly name: string;
  readonly mime_type: string;
  readonly size_bytes: number;
  readonly modified_time: string | null;
  readonly created_time: string | null;
  readonly parent_folder_id: string | null;
  readonly web_view_link: string | null;
  readonly is_folder: boolean;
}

export interface SharePointFilesResponse {
  readonly files: readonly SharePointFile[];
  readonly next_page_token: string | null;
  readonly total_count: number;
}

export async function listSharePointFiles(
  userId: string,
  siteId: string,
  driveId?: string,
  folderId?: string,
  connectionKey?: string,
): Promise<SharePointFilesResponse> {
  const params = new URLSearchParams({
    user_id: userId,
    provider: "sharepoint",
    site_id: siteId,
  });
  if (driveId) params.set("drive_id", driveId);
  if (folderId) params.set("folder_id", folderId);
  if (connectionKey) params.set("connection_key", connectionKey);
  return apiClient.get<SharePointFilesResponse>(
    `/drive/files?${params.toString()}`,
  );
}

/**
 * Adapt a SharePoint ``listSharePointFiles`` response into the shared
 * ``FolderTreeResponse`` shape consumed by the picker. The first level
 * of folders becomes the picker's roots — V1 used the same single-level
 * model for SharePoint (``buildSharePointFolderTree``) because Graph
 * SharePoint endpoints don't support a bulk tree walk like OneDrive's
 * delta. Sub-folder browsing in V1 was reactive (lazy on expand); V2's
 * ``<FolderTreeList>`` rendering today doesn't drill deeper either —
 * we ship parity now and add lazy children in a follow-up.
 */
export function sharePointFilesToFolderTree(
  response: SharePointFilesResponse,
): FolderTreeResponse {
  const folders = response.files.filter((f) => f.is_folder);
  return {
    nodes: folders.map((f) => ({
      id: f.file_id,
      name: f.name,
      parent_id: f.parent_folder_id,
      children_count: 0,
      has_children: false,
    })),
    roots: folders.map((f) => f.file_id),
    cached: false,
    stale: false,
  };
}

export async function listSharePointSites(
  userId: string,
  connectionKey?: string,
): Promise<readonly SharePointSite[]> {
  const params = new URLSearchParams({ user_id: userId });
  if (connectionKey) params.set("connection_key", connectionKey);
  const res = await apiClient.get<{ sites: readonly RawSharePointSite[] }>(
    `/drive/sharepoint/sites?${params.toString()}`,
  );
  return res.sites.map((s) => ({
    id: s.site_id,
    name: s.name,
    web_url: s.web_url ?? null,
    display_name: s.display_name ?? null,
    description: s.description ?? null,
  }));
}

export interface SharePointDrive {
  readonly id: string;
  readonly name: string;
  readonly drive_type?: string;
  readonly web_url?: string | null;
}

interface RawSharePointDrive {
  readonly drive_id: string;
  readonly name: string;
  readonly drive_type?: string;
  readonly web_url?: string;
  readonly quota_used?: number | null;
  readonly quota_total?: number | null;
}

/**
 * GET ``/drive/sharepoint/sites/{site_id}/drives?user_id=&connection_key=``.
 *
 * BE path-based (drive.py:884) — ``site_id`` is part of the URL, not a
 * query param. Response: ``{drives: [{drive_id, name, drive_type,
 * web_url, quota_used, quota_total}], total_count}``.
 */
export async function listSharePointDrives(
  userId: string,
  siteId: string,
  connectionKey?: string,
): Promise<readonly SharePointDrive[]> {
  const params = new URLSearchParams({ user_id: userId });
  if (connectionKey) params.set("connection_key", connectionKey);
  const res = await apiClient.get<{ drives: readonly RawSharePointDrive[] }>(
    `/drive/sharepoint/sites/${encodeURIComponent(siteId)}/drives?${params.toString()}`,
  );
  return res.drives.map((d) => ({
    id: d.drive_id,
    name: d.name,
    drive_type: d.drive_type,
    web_url: d.web_url ?? null,
  }));
}

// Notion picker (DOM overlay, no native SDK).

export interface NotionPickablePage {
  readonly id: string;
  readonly title: string;
  /** ``page`` / ``database`` — UI uses different icons. */
  readonly type: "page" | "database";
  readonly icon?: string | null;
  readonly url?: string | null;
  readonly last_edited_time?: string | null;
  readonly parent_id?: string | null;
  readonly is_selected?: boolean;
}

/** BE response shape (notion.py:461). */
interface RawNotionWorkspaceInfoResponse {
  readonly workspace_id?: string;
  readonly workspace_name?: string;
  readonly pages: readonly NotionPickablePage[];
  readonly databases: readonly NotionPickablePage[];
}

/**
 * GET ``/notion/workspace/pages?user_id=``.
 *
 * BE returns a NESTED ``{pages, databases}`` response shape
 * (notion.py:390). The adapter flattens to a single ordered list so
 * the picker UI can render one ``NotionPickerModal`` list without
 * per-type branching. The ``type`` discriminator on each item drives
 * the icon choice.
 *
 * ``search`` is a client-side hint — the BE endpoint doesn't actually
 * accept a search query (the Notion OAuth grants access to a curated
 * set of pages at consent time, so the list is bounded), but we keep
 * the parameter so the caller signature is stable if the BE adds it
 * later. Until then the FE filters in-memory.
 */
export async function searchNotionWorkspace(
  userId: string,
  _search?: string,
): Promise<readonly NotionPickablePage[]> {
  const params = new URLSearchParams({ user_id: userId });
  const res = await apiClient.get<RawNotionWorkspaceInfoResponse>(
    `/notion/workspace/pages?${params.toString()}`,
  );
  return [...(res.pages ?? []), ...(res.databases ?? [])];
}
